import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RotateCcw, Trophy, Share2, Download, Users, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Flag } from "../components/Flag";
import { fetchLineup, ogCardUrl } from "../lib/api";
import { useMatchSource } from "../hooks/useMatchSource";

const STADIUM_IMG =
  "https://images.unsplash.com/photo-1599158150601-1417ebbaafdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBzdGFkaXVtJTIwZGFyayUyMG5pZ2h0JTIwZHJhbWF0aWN8ZW58MHx8fHwxNzgxMDMwMTg4fDA&ixlib=rb-4.1.0&q=85";

const EVENT_ICON = {
  goal: "⚽",
  yellow_card: "🟨",
  red_card: "🟥",
  substitution: "🔄",
  penalties: "🥅",
};

// Reusable motion configs (stable refs, avoid re-renders)
const FADE_IN_Y = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
const FADE_IN = { initial: { opacity: 0 }, animate: { opacity: 1 } };
const SLIDE_IN_X = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.3 },
};

export default function Match() {
  const [params] = useSearchParams();
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const teamA = params.get("a");
  const teamB = params.get("b");
  const knockout = params.get("k") === "1";
  const isReplay = Boolean(routeId);

  const [version, setVersion] = useState(0);
  const { meta, events, currentMinute, final, status, error, matchId } = useMatchSource({
    routeId,
    teamA,
    teamB,
    knockout,
    version,
  });
  const timelineRef = useRef(null);

  // Derive score from events (single source of truth)
  const score = useMemo(() => {
    if (!meta) return { a: 0, b: 0 };
    let a = 0;
    let b = 0;
    for (const ev of events) {
      if (ev.type !== "goal") continue;
      if (ev.team === meta.team_a.code) a += 1;
      else if (ev.team === meta.team_b.code) b += 1;
    }
    return { a, b };
  }, [events, meta]);

  // Auto-scroll timeline to latest event
  useEffect(() => {
    const el = timelineRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [events.length]);

  const replay = () => {
    setVersion((v) => v + 1);
  };

  if (!routeId && (!teamA || !teamB)) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <h2 className="font-display text-3xl font-extrabold uppercase">No match selected</h2>
        <Link
          to="/"
          className="mt-6 inline-block bg-volt px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-black"
        >
          ← Back to selector
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      {/* Top action bar */}
      <div className="border-b border-zinc-900 bg-black/70">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            className="rounded-none font-display text-xs font-bold uppercase tracking-widest text-zinc-400 hover:bg-zinc-900 hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-3">
            {status === "live" && (
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-redz">
                <span className="live-dot" /> LIVE
              </span>
            )}
            {status === "done" && (
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                FULL-TIME
              </span>
            )}
            <Button
              onClick={replay}
              data-testid="replay-btn"
              disabled={status === "loading" || isReplay}
              className="rounded-none bg-zinc-900 px-4 font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-volt hover:text-black disabled:opacity-50"
            >
              <RotateCcw className="mr-2 h-3.5 w-3.5" /> Re-Simulate
            </Button>
            <ShareControls matchId={matchId} disabled={status !== "done"} />
          </div>
        </div>
      </div>

      {/* Scoreboard hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${STADIUM_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-10">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-6">
            <TeamSide team={meta?.team_a} score={score.a} side="a" />
            <div className="text-center">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
                {currentMinute > 0 ? `${currentMinute}'` : "KICKOFF"}
              </div>
              <div className="font-display text-2xl font-black uppercase tracking-tight text-zinc-500">
                VS
              </div>
              {meta?.knockout && (
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Knockout
                </div>
              )}
            </div>
            <TeamSide team={meta?.team_b} score={score.b} side="b" />
          </div>

          {meta?.stadium && (
            <p className="mx-auto mt-8 max-w-3xl text-center font-body text-sm italic text-zinc-300 sm:text-base">
              &ldquo;{meta.stadium}&rdquo;
            </p>
          )}
        </div>
      </section>

      {/* Algorithmic breakdown */}
      {meta && (
        <section className="border-b border-zinc-900 bg-zinc-950">
          <div className="mx-auto max-w-[1400px] px-6 py-8">
            <div className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Algorithmic Matchup Breakdown
            </div>
            <div className="grid grid-cols-1 gap-px bg-zinc-800 md:grid-cols-2">
              <BreakdownCard team={meta.team_a} text={meta.breakdown_a} />
              <BreakdownCard team={meta.team_b} text={meta.breakdown_b} />
            </div>
            <MssBars a={meta.team_a} b={meta.team_b} />
          </div>
        </section>
      )}

      {/* Timeline + Stats */}
      <section className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          {/* Timeline */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                Match Timeline
              </div>
              <div className="font-display text-lg font-bold uppercase text-zinc-400">
                {events.length} <span className="text-zinc-600">EVENTS</span>
              </div>
            </div>
            <div
              ref={timelineRef}
              data-testid="match-timeline"
              className="max-h-[640px] overflow-y-auto border border-zinc-900 bg-black"
            >
              {status === "loading" && (
                <div className="flex items-center gap-3 p-6">
                  <span className="live-dot" />
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                    Calibrating MSS · Querying Claude Sonnet 4.5 · Building lineups…
                  </span>
                </div>
              )}
              {status === "error" && (
                <div className="p-6 font-body text-sm text-redz">
                  Error: {error}. Try again.
                </div>
              )}
              <AnimatePresence initial={false}>
                {events.map((ev, idx) => (
                  <TimelineEvent
                    key={`${ev.minute}-${ev.type}-${ev.team || "_"}-${idx}`}
                    ev={ev}
                    meta={meta}
                  />
                ))}
              </AnimatePresence>
              {final && (
                <motion.div
                  {...FADE_IN_Y}
                  className="border-t-2 border-volt bg-zinc-950 p-6"
                >
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
                    FULL TIME
                  </div>
                  <div className="mt-2 font-display text-3xl font-black uppercase">
                    {meta?.team_a?.name} {final.score.a} – {final.score.b} {meta?.team_b?.name}
                  </div>
                  {final.penalties && final.pens && (
                    <div className="mt-1 font-mono text-sm text-zinc-400">
                      Penalties: {final.pens.a} – {final.pens.b}
                    </div>
                  )}
                  {final.extra_time && !final.penalties && (
                    <div className="mt-1 font-mono text-sm text-zinc-400">After Extra Time</div>
                  )}
                  {final.man_of_the_match && final.man_of_the_match !== "—" && (
                    <div className="mt-4 flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-volt" />
                      <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                        Man of the Match:
                      </span>
                      <span className="font-display text-base font-bold uppercase">
                        {final.man_of_the_match}
                      </span>
                    </div>
                  )}
                </motion.div>
              )}
              {final?.penalties && final.pen_sequence && (
                <PenaltyShootout meta={meta} final={final} live={status === "live"} />
              )}
            </div>
          </div>

          {/* Stats sidebar */}
          <div className="space-y-4">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Key Match Statistics
            </div>
            {final ? (
              <StatsCard meta={meta} final={final} />
            ) : (
              <div className="border border-zinc-900 bg-black p-6 text-sm text-zinc-500">
                Stats unlock at full-time.
              </div>
            )}
            {meta?.team_a && (
              <LineupPanel team={meta.team_a} side="HOME" />
            )}
            {meta?.team_b && (
              <LineupPanel team={meta.team_b} side="AWAY" />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function TeamSide({ team, score, side }) {
  return (
    <div className={`flex items-center gap-4 ${side === "b" ? "flex-row-reverse text-right" : ""}`}>
      {team && <Flag code={team.flag} className="h-10 w-14 object-cover sm:h-14 sm:w-20" />}
      <div className={side === "b" ? "text-right" : ""}>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {team?.code || "—"}
        </div>
        <div className="font-display text-2xl font-black uppercase leading-tight sm:text-4xl lg:text-5xl">
          {team?.name || "..."}
        </div>
        <div className="font-mono text-xs text-zinc-500">
          MSS <span className="text-volt">{team?.mss?.mss?.toFixed(1) || "--"}</span>
        </div>
      </div>
      <div className={`flex items-center ${side === "b" ? "order-first" : ""}`}>
        <span className="font-display text-6xl font-black tabular-nums sm:text-7xl lg:text-8xl">
          {score}
        </span>
      </div>
    </div>
  );
}

function BreakdownCard({ team, text }) {
  if (!team) return null;
  return (
    <div className="bg-zinc-950 p-6">
      <div className="mb-2 flex items-center gap-3">
        <Flag code={team.flag} className="h-5 w-7 object-cover" />
        <span className="font-display text-lg font-extrabold uppercase">{team.name}</span>
        <span className="ml-auto font-mono text-xs text-volt">MSS {team.mss.mss.toFixed(1)}</span>
      </div>
      <p className="font-body text-sm leading-relaxed text-zinc-300">{text || "—"}</p>
    </div>
  );
}

function MssBars({ a, b }) {
  const labels = [
    ["squad_value", "Squad Value"],
    ["top5_presence", "Top-5 Leagues"],
    ["powerhouse", "Pedigree"],
    ["age_curve", "Age Curve"],
    ["form", "Form"],
  ];
  return (
    <div className="mt-4 border border-zinc-900 bg-black p-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {labels.map(([key, label]) => (
          <div key={key}>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {label}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-10 font-display text-sm font-bold tabular-nums text-bluez">
                {a.mss.components[key].toFixed(0)}
              </span>
              <div className="relative h-2 flex-1 bg-zinc-900">
                <div
                  className="absolute left-0 top-0 h-full bg-bluez"
                  style={{ width: `${Math.max(2, a.mss.components[key] / 2)}%` }}
                />
                <div
                  className="absolute right-0 top-0 h-full bg-redz"
                  style={{ width: `${Math.max(2, b.mss.components[key] / 2)}%` }}
                />
              </div>
              <span className="w-10 text-right font-display text-sm font-bold tabular-nums text-redz">
                {b.mss.components[key].toFixed(0)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineEvent({ ev, meta }) {
  const isA = ev.team === meta?.team_a?.code;
  const teamName = isA ? meta?.team_a?.name : meta?.team_b?.name;
  const isGoal = ev.type === "goal";
  return (
    <motion.div
      {...SLIDE_IN_X}
      className={`flex gap-4 border-b border-zinc-900 px-5 py-4 ${isGoal ? "flash-volt" : ""}`}
      data-testid={`event-${ev.type}-${ev.minute}`}
    >
      <div className="flex w-14 flex-col items-center">
        <span className="font-display text-2xl font-black tabular-nums text-volt">
          {ev.minute}&apos;
        </span>
      </div>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">{EVENT_ICON[ev.type] || "·"}</span>
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          {teamName && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {teamName}
            </span>
          )}
          <span className="font-display text-base font-bold uppercase">
            {ev.player || ev.type.replace("_", " ")}
          </span>
          {isGoal && ev.assist && (
            <span className="font-mono text-xs text-zinc-500">
              · assist {ev.assist}
            </span>
          )}
        </div>
        {ev.description && (
          <p className="mt-1 font-body text-sm text-zinc-400">{ev.description}</p>
        )}
      </div>
    </motion.div>
  );
}

function StatsCard({ meta, final }) {
  const rows = [
    ["Possession %", final.possession.a, final.possession.b],
    ["Shots", final.shots.a, final.shots.b],
    ["Shots on Target", final.shots_on_target.a, final.shots_on_target.b],
    ["Expected Goals (xG)", final.xg.a, final.xg.b],
  ];
  return (
    <div className="space-y-3 border border-zinc-900 bg-black p-5">
      {rows.map(([label, av, bv]) => {
        const total = Number(av) + Number(bv) || 1;
        const aPct = (Number(av) / total) * 100;
        return (
          <div key={label}>
            <div className="mb-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <span>{label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-12 font-display text-base font-bold tabular-nums text-bluez">
                {typeof av === "number" && av % 1 ? av.toFixed(2) : av}
              </span>
              <div className="relative h-2 flex-1 bg-zinc-900">
                <div className="absolute left-0 top-0 h-full bg-bluez" style={{ width: `${aPct}%` }} />
              </div>
              <span className="w-12 text-right font-display text-base font-bold tabular-nums text-redz">
                {typeof bv === "number" && bv % 1 ? bv.toFixed(2) : bv}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function ShareControls({ matchId, disabled }) {
  const [copied, setCopied] = useState(false);
  if (!matchId) return null;
  const url = `${window.location.origin}/match/${matchId}`;
  const ogUrl = ogCardUrl(matchId);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error("Clipboard write failed:", e);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={copy}
        disabled={disabled}
        data-testid="share-link-btn"
        className="rounded-none bg-zinc-900 px-3 font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-volt hover:text-black disabled:opacity-40"
      >
        {copied ? <Check className="mr-2 h-3.5 w-3.5" /> : <Share2 className="mr-2 h-3.5 w-3.5" />}
        {copied ? "Copied" : "Share"}
      </Button>
      <a
        href={ogUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="download-og-btn"
        className={`flex items-center gap-2 border border-zinc-800 bg-zinc-900 px-3 py-2 font-display text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-volt hover:text-black ${
          disabled ? "pointer-events-none opacity-40" : ""
        }`}
      >
        <Download className="h-3.5 w-3.5" /> Card
      </a>
    </div>
  );
}

function PenaltyShootout({ meta, final, live }) {
  const seq = final.pen_sequence || [];
  const [revealed, setRevealed] = useState(live ? 0 : seq.length);

  useEffect(() => {
    if (!live) {
      setRevealed(seq.length);
      return undefined;
    }
    if (revealed >= seq.length) return undefined;
    const t = setTimeout(() => setRevealed((r) => r + 1), 900);
    return () => clearTimeout(t);
  }, [revealed, seq.length, live]);

  const codeA = meta?.team_a?.code;
  const codeB = meta?.team_b?.code;

  // Build rows: per round, slot for A and B
  const maxRound = Math.max(...seq.map((k) => k.round), 5);
  const rows = [];
  for (let r = 1; r <= maxRound; r += 1) {
    const a = seq.find((k) => k.round === r && k.team === codeA);
    const b = seq.find((k) => k.round === r && k.team === codeB);
    rows.push({ round: r, a, b });
  }

  const revealedKicks = new Set(seq.slice(0, revealed).map((k, i) => i));
  let cumA = 0;
  let cumB = 0;
  for (let i = 0; i < revealed; i += 1) {
    if (seq[i].team === codeA && seq[i].scored) cumA += 1;
    if (seq[i].team === codeB && seq[i].scored) cumB += 1;
  }

  return (
    <motion.div
      {...FADE_IN}
      className="border-t-2 border-redz bg-black"
      data-testid="penalty-shootout"
    >
      <div className="flex items-center justify-between border-b border-zinc-900 px-6 py-3">
        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-redz">
          <span className="live-dot" /> Penalty Shootout
        </span>
        <span className="font-display text-2xl font-black tabular-nums text-volt">
          {cumA} – {cumB}
        </span>
      </div>
      <div className="grid grid-cols-[120px_1fr_60px_1fr_120px] items-center gap-3 px-6 py-4">
        <div className="text-right font-display text-sm font-bold uppercase">{meta?.team_a?.name}</div>
        <div className="flex flex-wrap justify-end gap-2">
          {rows.map((row) => (
            <PenDot
              key={`a-${row.round}`}
              kick={row.a}
              shown={row.a && revealedKicks.has(seq.indexOf(row.a))}
              side="a"
            />
          ))}
        </div>
        <div className="text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          ROUND
        </div>
        <div className="flex flex-wrap gap-2">
          {rows.map((row) => (
            <PenDot
              key={`b-${row.round}`}
              kick={row.b}
              shown={row.b && revealedKicks.has(seq.indexOf(row.b))}
              side="b"
            />
          ))}
        </div>
        <div className="font-display text-sm font-bold uppercase">{meta?.team_b?.name}</div>
      </div>
      {revealed > 0 && revealed <= seq.length && (
        <div className="border-t border-zinc-900 px-6 py-3 font-mono text-xs text-zinc-400">
          <span className="text-volt">
            Kick {revealed} of {seq.length}:
          </span>{" "}
          {seq[revealed - 1].player} ({seq[revealed - 1].team}) ·{" "}
          {seq[revealed - 1].scored ? (
            <span className="text-green-400">SCORED</span>
          ) : (
            <span className="text-redz">MISSED</span>
          )}
        </div>
      )}
    </motion.div>
  );
}

function PenDot({ kick, shown, side }) {
  if (!kick) {
    return <span className="inline-block h-5 w-5 border border-dashed border-zinc-800" />;
  }
  if (!shown) {
    return (
      <span className="inline-block h-5 w-5 animate-pulse border border-zinc-700 bg-zinc-900" />
    );
  }
  return (
    <span
      data-testid={`pen-${side}-${kick.round}-${kick.scored ? "scored" : "missed"}`}
      title={`${kick.player} - ${kick.scored ? "Scored" : "Missed"}`}
      className={`inline-flex h-5 w-5 items-center justify-center text-[11px] font-black ${
        kick.scored ? "bg-volt text-black" : "border border-redz bg-black text-redz"
      }`}
    >
      {kick.scored ? "●" : "×"}
    </span>
  );
}

function LineupPanel({ team, side }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) {
      setLoading(true);
      try {
        const lineup = await fetchLineup(team.code);
        setData(lineup);
      } catch (e) {
        console.error("Lineup fetch failed for", team.code, e);
        setData({ formation: "—", manager: "—", players: [] });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="border border-zinc-900 bg-black" data-testid={`lineup-${team.code}`}>
      <button
        onClick={toggle}
        className="flex w-full items-center justify-between px-5 py-3 hover:bg-zinc-950"
        data-testid={`lineup-toggle-${team.code}`}
      >
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4 text-volt" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{side}</span>
          <span className="font-display text-sm font-bold uppercase">{team.name} XI</span>
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {open ? "Close" : "View"}
        </span>
      </button>
      {open && (
        <div className="border-t border-zinc-900 px-5 py-4">
          {loading && (
            <div className="font-mono text-xs text-zinc-500">Generating lineup…</div>
          )}
          {data && (
            <>
              <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <span>
                  Formation <span className="text-volt">{data.formation}</span>
                </span>
                <span>Mgr: {data.manager}</span>
              </div>
              <div className="space-y-1.5">
                {data.players.map((p) => (
                  <div
                    key={p.number}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-7 text-center font-mono text-xs text-zinc-500">
                        {p.number}
                      </span>
                      <span className="w-9 text-center font-mono text-[10px] uppercase tracking-widest text-volt">
                        {p.pos}
                      </span>
                      <span className="font-display text-sm font-bold uppercase">{p.name}</span>
                      <span className="text-xs text-zinc-500">· {p.club}</span>
                    </div>
                    <span className="font-mono text-xs font-bold tabular-nums text-volt">
                      {p.rating}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
